import { Fragment, useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useLang } from '@/i18n/LangContext';
import { LANGS, LOCALES, localePath } from '@/lib/seo-data.mjs';

/**
 * The nav's logo mark (the roundel only; the wordmark sits beside it).
 *
 * Versioned on purpose. Cloudflare serves `/og/` to browsers with
 * `Cache-Control: max-age=2678400` (31 days; its Browser Cache TTL overrides the
 * origin's 1 day, measured 2026-09-30), and the query string is part of its
 * cache key. A same-URL swap would leave returning visitors on the old mark for
 * up to a month. Bump `v` whenever this file's bytes change
 * (`scripts/build_icons.py` regenerates it).
 */
const BRAND_MARK_SRC = '/og/logo-192-transparent.png?v=2';

type NavMode = 'dark' | 'light';

interface NavProps {
  /** The nav's mode at the top of the page (scrollY = 0). */
  initialMode?: NavMode;
  /**
   * The nav's mode after scrolling past ~92 % of viewport height. If omitted
   * or equal to `initialMode`, the nav keeps `initialMode` for the whole page.
   *
   * Common patterns:
   *   - Home / Location / Availability: `initialMode='dark'`,
   *     `scrolledMode='light'` — navy nav over the dark hero, cream nav over
   *     the cream sections below.
   *   - Yacht: `initialMode='light'`, `scrolledMode='dark'` — cream nav over
   *     the navy hero (intentional inverse, operator preference 2026-05-24),
   *     navy nav below.
   */
  scrolledMode?: NavMode;
}

/**
 * The language switch: one real, crawlable link per locale to the SAME page in that
 * language, keeping the query and the fragment (`/yacht?x=1#gallery` ⇄
 * `/it/yacht?x=1#gallery`). The current language is marked with `aria-current`.
 */
function LangToggle({ className }: { className: string }) {
  const { lang, basePath } = useLang();
  const { search, hash } = useLocation();
  return (
    <div className={className} role="group" aria-label="Language">
      {LANGS.map((l, i) => (
        <Fragment key={l}>
          {i > 0 && <span className="sep">/</span>}
          <Link
            to={`${localePath(basePath, l)}${search}${hash}`}
            className={l === lang ? 'on' : ''}
            hrefLang={LOCALES[l].hreflang}
            lang={LOCALES[l].htmlLang}
            aria-current={l === lang ? 'true' : undefined}
          >
            {l.toUpperCase()}
          </Link>
        </Fragment>
      ))}
    </div>
  );
}

export function Nav({ initialMode = 'dark', scrolledMode }: NavProps) {
  const { copy, lang, basePath } = useLang();
  const effectiveScrolled = scrolledMode ?? initialMode;
  const [light, setLight] = useState(initialMode === 'light');
  const [menuOpen, setMenuOpen] = useState(false);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (effectiveScrolled === initialMode) {
      // No transition — stay in initialMode for the whole page.
      setLight(initialMode === 'light');
      return;
    }
    const onScroll = () => {
      const past = window.scrollY > window.innerHeight * 0.92;
      const mode: NavMode = past ? effectiveScrolled : initialMode;
      setLight(mode === 'light');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [initialMode, effectiveScrolled]);

  // Dismiss the mobile menu on navigation (frontend-patterns §10). Keyed on the
  // locale-stripped path: a language switch is not a navigation away, so the menu
  // stays open, as it did when the switch was a button.
  useEffect(() => {
    setMenuOpen(false);
  }, [basePath]);

  // While the menu is open: lock background scroll, trap Tab within the
  // overlay, close on Escape, and restore focus to the trigger on close.
  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusable = () =>
      Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [],
      );
    focusable()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    // Close (and release the scroll lock) if the viewport grows past the mobile
    // breakpoint while the menu is open — otherwise the menu becomes
    // `display:none` but the lock and open state would dangle.
    const desktopQuery = window.matchMedia('(min-width: 901px)');
    const onViewportChange = () => {
      if (desktopQuery.matches) setMenuOpen(false);
    };
    desktopQuery.addEventListener('change', onViewportChange);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      desktopQuery.removeEventListener('change', onViewportChange);
      document.body.style.overflow = previousOverflow;
      burgerRef.current?.focus();
    };
  }, [menuOpen]);

  return (
    <>
      <header className={`nav ${light ? 'is-light' : ''}`}>
        <Link to={localePath('/', lang)} className="brand" aria-label="Virtus — home">
          <img
            src={BRAND_MARK_SRC}
            alt=""
            aria-hidden="true"
            width={40}
            height={40}
            className="brand-logo"
          />
          <span className="brand-words">
            VIRTUS
            <small>Charter · Porto Cervo</small>
          </span>
        </Link>
        <nav className="nav-links" aria-label="Primary">
          <NavLink to={localePath('/yacht', lang)} className={({ isActive }) => (isActive ? 'active' : '')}>
            {copy.nav.yacht}
          </NavLink>
          <NavLink to={localePath('/location', lang)} className={({ isActive }) => (isActive ? 'active' : '')}>
            {copy.nav.location}
          </NavLink>
          <NavLink to={localePath('/availability', lang)} className={({ isActive }) => (isActive ? 'active' : '')}>
            {copy.nav.availability}
          </NavLink>
        </nav>
        <LangToggle className="lang-toggle" />
        <button
          ref={burgerRef}
          type="button"
          className="nav-burger"
          aria-label={copy.nav.menuOpen}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      <div
        id="mobile-menu"
        ref={menuRef}
        className={`mobile-menu ${menuOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={copy.nav.menu}
        aria-hidden={!menuOpen}
      >
        <div className="mobile-menu-top">
          <Link to={localePath('/', lang)} className="brand" aria-label="Virtus — home">
            <img
              src={BRAND_MARK_SRC}
              alt=""
              aria-hidden="true"
              width={40}
              height={40}
              className="brand-logo"
            />
            <span className="brand-words">
              VIRTUS
              <small>Charter · Porto Cervo</small>
            </span>
          </Link>
          <button
            type="button"
            className="mobile-menu-close"
            aria-label={copy.nav.menuClose}
            onClick={() => setMenuOpen(false)}
          >
            <span />
            <span />
          </button>
        </div>
        <nav className="mobile-menu-links" aria-label="Primary">
          <NavLink to={localePath('/yacht', lang)} className={({ isActive }) => (isActive ? 'active' : '')}>
            {copy.nav.yacht}
          </NavLink>
          <NavLink to={localePath('/location', lang)} className={({ isActive }) => (isActive ? 'active' : '')}>
            {copy.nav.location}
          </NavLink>
          <NavLink to={localePath('/availability', lang)} className={({ isActive }) => (isActive ? 'active' : '')}>
            {copy.nav.availability}
          </NavLink>
        </nav>
        <LangToggle className="mobile-menu-lang lang-toggle" />
      </div>
    </>
  );
}
